import { useState, useEffect, useCallback, useRef } from "react";
import { customFetch } from "@workspace/api-client-react";

type PushState = "unsupported" | "default" | "granted" | "denied" | "loading";

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua);
  const isIPadOnMac =
    /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
  return isAppleMobile || isIPadOnMac;
}

function isStandalonePWA(): boolean {
  if (typeof window === "undefined") return false;
  if ((navigator as any).standalone === true) return true;
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

interface PrepareResult {
  reg: ServiceWorkerRegistration;
  publicKey: string;
}

async function prepareServiceWorker(): Promise<PrepareResult | null> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const swPath = `${import.meta.env.BASE_URL}sw.js`;
  const reg = await navigator.serviceWorker.register(swPath);
  await navigator.serviceWorker.ready;
  const { publicKey } = await customFetch<{ publicKey?: string }>("/api/push/vapid-public-key");
  if (!publicKey) return null;
  return { reg, publicKey };
}

async function ensurePushSubscription(
  reg: ServiceWorkerRegistration,
  publicKey: string,
): Promise<PushSubscriptionJSON | null> {
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }
  return subscription.toJSON();
}

// Garante que o servidor tenha a subscription atual deste dispositivo.
// Idempotente (o backend faz onConflictDoUpdate no endpoint). Reusado na
// montagem do hook pra curar o desencontro "selo verde mas sent=0": o
// navegador mantém uma subscription local válida enquanto o servidor já
// apagou a linha (o FCM devolve 410 em expiração transitória e o backend
// remove em push.ts). Sem este re-sync o lojista vê "ativadas" e perde pedido.
async function registerSubscriptionWithServer(): Promise<boolean> {
  const prepared = await prepareServiceWorker();
  if (!prepared) return false;
  const json = await ensurePushSubscription(prepared.reg, prepared.publicKey);
  if (!json) return false;
  await customFetch("/api/push/subscribe", {
    method: "POST",
    body: JSON.stringify({
      endpoint: json.endpoint,
      keysP256dh: json.keys?.p256dh,
      keysAuth: json.keys?.auth,
    }),
  });
  return true;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>("loading");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("unsupported");
      return;
    }

    if (isIOS() && !isStandalonePWA()) {
      setState("unsupported");
      return;
    }

    const permission = Notification.permission;
    setState(permission as PushState);

    if (permission === "granted") {
      registerSubscriptionWithServer()
        .then((ok) => setIsSubscribed(ok))
        .catch(() => setIsSubscribed(false));
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("unsupported");
      return false;
    }
    if (isIOS() && !isStandalonePWA()) {
      setState("unsupported");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setState(permission as PushState);
      if (permission !== "granted") return false;

      const ok = await registerSubscriptionWithServer();
      setIsSubscribed(ok);
      return ok;
    } catch (err) {
      console.error("Push subscription failed:", err);
      return false;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await customFetch("/api/push/unsubscribe", {
          method: "POST",
          body: JSON.stringify({ endpoint }),
        });
      }
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
      return false;
    }
  }, []);

  return {
    state,
    isSubscribed,
    subscribe,
    unsubscribe,
    iosNeedsPwa: isIOS() && !isStandalonePWA(),
  };
}

export async function subscribeCustomerPush(
  storeId: string | undefined,
  options: { requestPermission?: boolean } = {},
): Promise<boolean> {
  if (!storeId) return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  if (isIOS() && !isStandalonePWA()) return false;

  try {
    if (options.requestPermission && Notification.permission === "default") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return false;
    }
    if (Notification.permission !== "granted") return false;

    const prepared = await prepareServiceWorker();
    if (!prepared) return false;

    const json = await ensurePushSubscription(prepared.reg, prepared.publicKey);
    if (!json) return false;

    await customFetch("/api/push/subscribe/customer", {
      method: "POST",
      body: JSON.stringify({
        endpoint: json.endpoint,
        keysP256dh: json.keys?.p256dh,
        keysAuth: json.keys?.auth,
        storeId,
      }),
    });
    return true;
  } catch (err) {
    console.error("Customer push subscription failed:", err);
    return false;
  }
}

export function useCustomerPushSubscription(
  storeId: string | undefined,
  customerId: string | undefined,
) {
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!storeId || !customerId) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (Notification.permission !== "granted") return;

    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setIsSubscribed(!!sub))
      .catch(() => setIsSubscribed(false));
  }, [storeId, customerId]);

  const subscribe = useCallback(async () => {
    const ok = await subscribeCustomerPush(storeId, { requestPermission: true });
    if (ok) setIsSubscribed(true);
    return ok;
  }, [storeId]);

  return { isSubscribed, subscribe };
}
